import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createExtCustomColumn,
    deleteExtCustomColumn,
    getExtCustomColumn,
    resetCustomColumnForm,
    setErrors,
    setExtCustomColumn,
    updateExtCustomColumn,
    updateExtCustomColumnsOrder,
    setOpenDrawer
} from '../../store/extCustomColumns/actions';
import { isValidObjectId } from '../../commonUtils/string.helper';
import { setExtVersion } from '../../store/extVersions/actions';
import { setOpenModal } from '../../store/extCustomColumnQueries/actions';
import { getAvailableFields } from '../../commonUtils/queryBuilder.helper';
import * as extractionActionTypes from '../../store/extractions/actionTypes';
import constants from '../../commonUtils/constants';


const ExtCustomColumnsContext = createContext();

export const useExtCustomColumns = () => useContext(ExtCustomColumnsContext);

// Fields that are permanently selected and cannot be toggled or removed by the user.
export const PINNED_FIELDS = new Set(constants.extraction.mandatoryColumns);
const NON_REPLACEABLE_FIELDS = new Set(constants.extraction.mandatoryColumns);

const getColumnUsedFields = (col) => {
    if (!col) return [];
    if (Array.isArray(col.used_fields)) return col.used_fields;
    if (Array.isArray(col.fields)) return col.fields;
    return [];
};

export const ExtCustomColumnsProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { columnListValues, customColumnOptions } = useSelector((state) => state.Extractions);
    const { ExtCustomColumnsList, loading, errors: extCustomColumnErrors, extCustomColumn } = useSelector((state) => state.ExtCustomColumns);
    const { extVersion } = useSelector((state) => state.ExtVersions);

    // -------------------------------------------------------------------------
    // Derived data
    // -------------------------------------------------------------------------
    const customColumnsDataMap = useMemo(() => {
        const map = {};
        (ExtCustomColumnsList?.data || []).forEach(col => {
            if (col && col._id) map[String(col._id)] = col;
        });
        return map;
    }, [ExtCustomColumnsList]);

    // -------------------------------------------------------------------------
    // ALL REFS — declared first so every useCallback/useEffect below can safely
    // read from them. JavaScript hoists var declarations but not const/let, so
    // order matters: refs must come before the callbacks that close over them.
    // -------------------------------------------------------------------------

    // Stable mirrors of frequently-changing Redux objects. Kept current by the
    // tiny sync effects immediately below. No callback or effect that reads these
    // needs to put them in its dep array — preventing re-runs on every API call.
    const customColumnsDataMapRef     = useRef(customColumnsDataMap);
    const customColumnOptionsRef      = useRef(customColumnOptions);
    const extVersionRef               = useRef(extVersion);

    // Guard refs — stop effects re-dispatching after their own Redux updates.
    const pinnedFieldsGuardRef        = useRef(null);
    const processedCustomColumnsRef   = useRef(null);

    // Duplicate-run guard for the replaceOptions effect.
    const prevSelectedColumnsRef      = useRef();
    const prevExtCustomColumnsDataRef = useRef();

    // Sync effects — the ONLY effects that run on every relevant render.
    // They never dispatch; they just mirror Redux state into refs.
    useEffect(() => { customColumnsDataMapRef.current     = customColumnsDataMap;      }, [customColumnsDataMap]);
    useEffect(() => { customColumnOptionsRef.current      = customColumnOptions;        }, [customColumnOptions]);
    useEffect(() => { extVersionRef.current               = extVersion;                 }, [extVersion]);

    // -------------------------------------------------------------------------
    // Local state
    // -------------------------------------------------------------------------
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId]                                     = useState('');
    const [localExtCustomColumns, setLocalExtCustomColumns]               = useState([]);
    const [replaceOptions, setReplaceOptions]                             = useState([]);

    // -------------------------------------------------------------------------
    // Derived memos
    // -------------------------------------------------------------------------
    const queryBuilderOptions = useMemo(() => {
        if (!extVersion?.selected_columns?.length) return [];
        const selectedSet = new Set(extVersion.selected_columns);
        const filtered = columnListValues.filter(col => col?.Field && selectedSet.has(col.Field));
        const uniqueMap = new Map();
        filtered.forEach(col => { if (!uniqueMap.has(col.Field)) uniqueMap.set(col.Field, col); });
        return Array.from(uniqueMap.values());
    }, [columnListValues, extVersion?.selected_columns]);

    const filteredCustomColumns = useMemo(() => {
        const selectedCustomColumnIds = extVersion?.custom_columns || [];
        if (!selectedCustomColumnIds.length) return [];

        const optionsById = new Map(
            (customColumnOptions || []).map((opt) => [String(opt.value || opt._id || opt.id), opt])
        );

        return selectedCustomColumnIds
            .map((id) => {
                const idStr = String(id);
                const raw = customColumnsDataMap[idStr] || optionsById.get(idStr) || null;
                if (!raw) return null;

                const normalizedId = raw._id || raw.id || raw.value;
                const normalizedValue = raw.value || raw.id || raw._id;
                const normalizedLabel = raw.label || raw.title || raw.name || String(normalizedValue || '');

                return {
                    ...raw,
                    _id: normalizedId,
                    value: normalizedValue,
                    label: normalizedLabel,
                };
            })
            .filter(Boolean);
    }, [extVersion?.custom_columns, customColumnsDataMap, customColumnOptions]);

    const customColumnSelectionMetadataKey = useMemo(() => {
        const selectedCustomColumnIds = (extVersion?.custom_columns || []).map(String);
        if (!selectedCustomColumnIds.length) return '[]';

        const optionsById = new Map(
            (customColumnOptions || []).map((opt) => [String(opt.value || opt._id || opt.id), opt])
        );

        return JSON.stringify(
            selectedCustomColumnIds.map((id) => {
                const col = customColumnsDataMap[id] || optionsById.get(id) || null;
                return {
                    id,
                    used_fields: getColumnUsedFields(col),
                    replace_with: Array.isArray(col?.replace_with) ? col.replace_with : [],
                };
            })
        );
    }, [extVersion?.custom_columns, customColumnsDataMap, customColumnOptions]);

    const allReplaceValues = useMemo(() => (
        [...new Set(extVersion?.selected_columns || [])].filter(
            (field) => !NON_REPLACEABLE_FIELDS.has(field)
        )
    ), [extVersion?.selected_columns]);

    const allReplaceWithValues = useMemo(() => {
        const set = new Set();
        (filteredCustomColumns || []).forEach((col) => {
            if (col && Array.isArray(col.replace_with)) {
                col.replace_with.forEach((field) => set.add(field));
            }
        });
        return set;
    }, [filteredCustomColumns]);

    const usedFieldsInSelectedCustomColumns = useMemo(() => {
        const set = new Set();
        (filteredCustomColumns || []).forEach((col) => {
            getColumnUsedFields(col).forEach((field) => set.add(field));
        });
        return set;
    }, [filteredCustomColumns]);

    const filteredSelectedColumns = useMemo(() => {
        const selected = extVersion?.selected_columns || [];
        return selected.filter(col => PINNED_FIELDS.has(col) || !allReplaceWithValues.has(col));
    }, [extVersion?.selected_columns, allReplaceWithValues]);

    const filteredCustomColumnOptions = useMemo(() => {
        if (!Array.isArray(customColumnOptions) || !extVersion?.custom_columns) return [];
        const editingId = extCustomColumn?.id;
        return customColumnOptions.filter(
            (option) => option.value === editingId || !extVersion.custom_columns.includes(option.value)
        );
    }, [customColumnOptions, extVersion?.custom_columns, extCustomColumn?.id]);

    const extCustomColumns = useMemo(() => (
        filteredCustomColumns?.map((item, index) => ({
            id: `${item._id}_${index}`,
            _id: item._id,
            key: item.key,
            title: item.title,
            query: item.query,
            fields: item.fields,
            replace_with: item.replace_with,
            originalIndex: index + 1,
        })) || []
    ), [filteredCustomColumns]);

    const pagination    = useMemo(() => ExtCustomColumnsList?.pagination, [ExtCustomColumnsList]);
    const availableFields = useMemo(() => getAvailableFields(columnListValues), [columnListValues]);

    const titleValidationArray = useMemo(() => {
        const ccOpt = (customColumnOptions || []).map(item => item.label);
        return [...new Set([...(availableFields || []), ...ccOpt])];
    }, [availableFields, customColumnOptions]);

    // -------------------------------------------------------------------------
    // Effects (dispatch-free sync effects already declared above)
    // -------------------------------------------------------------------------

    // Sync localExtCustomColumns from the memo — never call setState inside useMemo.
    useEffect(() => {
        // Keep local render state fully in sync, including transitions to empty.
        setLocalExtCustomColumns(extCustomColumns);
    }, [extCustomColumns]);

    // Keep replaceOptions in sync; guard prevents redundant sets.
    useEffect(() => {
        if (!extVersion?.selected_columns) return;
        const selectedColumnsStr = JSON.stringify(extVersion.selected_columns);
        const dataStr            = JSON.stringify(filteredCustomColumns);
        if (
            prevSelectedColumnsRef.current      === selectedColumnsStr &&
            prevExtCustomColumnsDataRef.current === dataStr
        ) return;
        prevSelectedColumnsRef.current      = selectedColumnsStr;
        prevExtCustomColumnsDataRef.current = dataStr;
        setReplaceOptions(
            [...new Set([...extVersion.selected_columns])].filter(
                (field) => !NON_REPLACEABLE_FIELDS.has(field)
            )
        );
    }, [extVersion?.selected_columns, filteredCustomColumns]);

    // Ensure pinned fields are always in selected_columns.
    // extVersion spread uses extVersionRef so extVersion doesn't need to be in
    // deps (SET_EXT_VERSION always produces a new object reference, so adding it
    // would cause this effect to re-run on every unrelated extVersion change).
    useEffect(() => {
        const selectedColumns = extVersion?.selected_columns || [];
        const missing = [...PINNED_FIELDS].filter(f => !selectedColumns.includes(f));
        if (!missing.length) return;

        const guardKey = JSON.stringify(selectedColumns);
        if (pinnedFieldsGuardRef.current === guardKey) return;
        pinnedFieldsGuardRef.current = guardKey;

        dispatch(setExtVersion({
            ...extVersionRef.current,
            selected_columns: [...missing, ...selectedColumns],
        }));
        dispatch({ type: extractionActionTypes.SAVE_TEMP_SELECTIONS });
    }, [extVersion?.selected_columns, dispatch]); // extVersion read via ref

    // Ensure custom column used_fields/replace_with are in selected_columns.
    // Data maps and extVersion read from refs — prevents re-runs on API responses.
    useEffect(() => {
        if (!extVersion?.custom_columns?.length) return;

        const guardKey = JSON.stringify({
            cc: extVersion.custom_columns,
            sc: extVersion.selected_columns,
            meta: customColumnSelectionMetadataKey,
        });
        if (processedCustomColumnsRef.current === guardKey) return;

        const currentCols = extVersion.selected_columns || [];
        let extras = [];

        extVersion.custom_columns.forEach(id => {
            const idStr = String(id);
            let col = customColumnsDataMapRef.current[idStr];
            if (!col) {
                col = customColumnOptionsRef.current.find(
                    o => String(o.value) === idStr || String(o._id) === idStr
                ) || null;
            }
            if (!col) return;
            extras = extras.concat(getColumnUsedFields(col));
            if (Array.isArray(col.replace_with))  extras = extras.concat(col.replace_with);
        });

        const merged = extras.length ? [...new Set([...currentCols, ...extras])] : currentCols;

        // Mark BEFORE dispatching so the re-render hits the guard immediately.
        processedCustomColumnsRef.current = guardKey;

        if (merged.length === currentCols.length) return;

        dispatch(setExtVersion({ ...extVersionRef.current, selected_columns: merged }));
        dispatch({ type: extractionActionTypes.SAVE_TEMP_SELECTIONS });
    }, [extVersion?.custom_columns, extVersion?.selected_columns, customColumnSelectionMetadataKey, dispatch]);

    // -------------------------------------------------------------------------
    // Handlers — all read extVersion/customColumnsDataMap via refs so they
    // don't need those objects in dep arrays and won't recreate on every render.
    // -------------------------------------------------------------------------
    const handleRemoveColumn = useCallback((fieldName) => {
        if (PINNED_FIELDS.has(fieldName)) return;
        const cur = extVersionRef.current;

        const isUsedBySelectedCustomColumn = (cur?.custom_columns || []).some((colId) => {
            const idStr = String(colId);
            const col =
                customColumnsDataMapRef.current[idStr] ||
                customColumnOptionsRef.current.find(
                    (opt) => String(opt.value) === idStr || String(opt._id) === idStr
                );
            return getColumnUsedFields(col).includes(fieldName);
        });

        if (isUsedBySelectedCustomColumn) return;

        const updatedSelected    = cur?.selected_columns.filter(f => f !== fieldName) || [];
        const remainingCustomCols = (cur?.custom_columns || []).filter(colId => {
            const col = customColumnsDataMapRef.current[String(colId)];
            if (!col) return true;
            return !getColumnUsedFields(col).includes(fieldName);
        });
        dispatch(setExtVersion({ ...cur, selected_columns: updatedSelected, custom_columns: remainingCustomCols }));
        dispatch({ type: extractionActionTypes.SAVE_TEMP_SELECTIONS });
    }, [dispatch]);

    const handleRemoveCustomColumn = useCallback((customColId) => {
        const cur = extVersionRef.current;
        dispatch(setExtVersion({ ...cur, custom_columns: cur?.custom_columns.filter(f => f !== customColId) }));
        dispatch({ type: extractionActionTypes.SAVE_TEMP_SELECTIONS });
    }, [dispatch]);

    const handleToggleColumn = useCallback((fieldName) => {
        if (PINNED_FIELDS.has(fieldName)) return;
        const cur = extVersionRef.current;
        const currentColumns = cur?.selected_columns || [];

        const isCurrentlySelected = currentColumns.includes(fieldName);
        const isUsedBySelectedCustomColumn = (cur?.custom_columns || []).some((colId) => {
            const idStr = String(colId);
            const col =
                customColumnsDataMapRef.current[idStr] ||
                customColumnOptionsRef.current.find(
                    (opt) => String(opt.value) === idStr || String(opt._id) === idStr
                );
            return getColumnUsedFields(col).includes(fieldName);
        });

        // Never allow unselecting a field required by any selected custom column.
        if (isCurrentlySelected && isUsedBySelectedCustomColumn) return;

        const updatedValue   = currentColumns.includes(fieldName)
            ? currentColumns.filter(f => f !== fieldName)
            : [...currentColumns, fieldName];
        dispatch(setExtVersion({ ...cur, selected_columns: updatedValue }));
        dispatch({ type: extractionActionTypes.SAVE_TEMP_SELECTIONS });
    }, [dispatch]);

    const handleToggleIsDefault = useCallback((isDefault) => {
        dispatch(setExtVersion({ ...extVersionRef.current, is_default: isDefault }));
    }, [dispatch]);

    const handleRowReorder = useCallback((reorderedData) => {
        const updatedData = reorderedData.map((item, index) => ({ ...item, priority: index + 1 }));
        setLocalExtCustomColumns(updatedData);
        dispatch(updateExtCustomColumnsOrder(updatedData.map((item, index) => ({ _id: item._id, priority: index + 1 }))));
    }, [dispatch]);

    const getRowId    = useCallback((row) => row.id?.toString() || row._id?.toString() || '', []);
    const resetOrder  = useCallback(() => setLocalExtCustomColumns([]), []);

    const editCustomColumn       = useCallback((id) => dispatch(getExtCustomColumn(id)), [dispatch]);
    const setOpenCustomColumnDrawer = useCallback((val) => dispatch(setOpenDrawer(val)), [dispatch]);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteExtCustomColumn(toDeleteId));
        setToDeleteId('');
        setShowDeleteConfirmationDialog(false);
    }, [dispatch, toDeleteId]);

    const fetchAllReplaceValues = useCallback((keyword = '') => (
        allReplaceValues.filter(v => v.toLowerCase().includes(keyword.toLowerCase()))
    ), [allReplaceValues]);

    const fetchReplaceOptions = useCallback((keyword = '') => {
        const cur = extVersionRef.current;
        if (!cur?.selected_columns) { setReplaceOptions([]); return; }
        const existingReplaceWith = filteredCustomColumns?.flatMap(col => col.replace_with || []) || [];
        const allColumns = [...new Set([...cur.selected_columns, ...existingReplaceWith])].filter(
            (field) => !NON_REPLACEABLE_FIELDS.has(field)
        );
        setReplaceOptions(allColumns.filter(f => f.toLowerCase().includes(keyword.toLowerCase())));
    }, [filteredCustomColumns]);

    const handleInputChange = useCallback((key, value) => {
        dispatch(setExtCustomColumn({ ...extCustomColumn, [key]: value }));
        if (key === 'title') {
            if (/\s/.test(value)) {
                dispatch(setErrors({ title: 'Custom column name cannot contain spaces.' }));
            } else if (isValidObjectId(value)) {
                dispatch(getExtCustomColumn(value));
            } else if (titleValidationArray.includes(value)) {
                dispatch(setErrors({ title: 'Column already Exists' }));
            } else {
                dispatch(setErrors({}));
            }
        }
    }, [dispatch, extCustomColumn, titleValidationArray]);

    const addNewCustomColumn = useCallback(() => dispatch(resetCustomColumnForm()), [dispatch]);

    const saveExtCustomColumn = useCallback(() => {
        if (Object.keys(extCustomColumnErrors || {}).length > 0) return;
        if (extCustomColumn.id) {
            dispatch(updateExtCustomColumn({
                id: extCustomColumn.id,
                formData: { title: extCustomColumn.title, replace_with: extCustomColumn.replace_with },
            }));
        } else {
            dispatch(createExtCustomColumn({ extCustomColumn }));
        }
    }, [dispatch, extCustomColumn, extCustomColumnErrors]);

    const addNewExtCustomColumnQuery = useCallback(() => {
        if (Object.keys(extCustomColumnErrors || {}).length > 0) return;
        if (!extCustomColumn.id && extCustomColumn.title) {
            dispatch(createExtCustomColumn({ extCustomColumn }));
        } else if (extCustomColumn.id) {
            dispatch(setOpenModal(true));
        }
    }, [dispatch, extCustomColumn, extCustomColumnErrors]);

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    const displayData = localExtCustomColumns.length > 0 ? localExtCustomColumns : extCustomColumns;

    return (
        <ExtCustomColumnsContext.Provider value={{
            extCustomColumns: displayData,
            loading,
            extCustomColumnErrors,
            editCustomColumn,
            handleInputChange,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
            handleRowReorder,
            getRowId,
            resetOrder,
            addNewCustomColumn,
            queryBuilderOptions,
            filteredSelectedColumns,
            filteredCustomColumns,
            pagination,
            availableFields,
            titleValidationArray,
            allReplaceValues,
            fetchAllReplaceValues,
            allReplaceWithValues,
            usedFieldsInSelectedCustomColumns,
            replaceOptions,
            setReplaceOptions,
            fetchReplaceOptions,
            saveExtCustomColumn,
            addNewExtCustomColumnQuery,
            setOpenCustomColumnDrawer,
            filteredCustomColumnOptions,
            handleRemoveColumn,
            handleRemoveCustomColumn,
            handleToggleColumn,
            handleToggleIsDefault,
            PINNED_FIELDS,
        }}>
            {children}
        </ExtCustomColumnsContext.Provider>
    );
};